import { SITE, absoluteUrl } from './site.mjs';

const CONTEXT = 'https://schema.org';

export const organizationSchema = () => ({
    '@context': CONTEXT,
    '@type': ['Organization', 'ProfessionalService'],
    '@id': `${SITE.url}/#organization`,
    name: SITE.name,
    legalName: SITE.legalName,
    url: `${SITE.url}/`,
    logo: { '@type': 'ImageObject', url: SITE.logo, width: 523, height: 160 },
    image: SITE.ogImage,
    description: SITE.defaultDescription,
    telephone: SITE.phone,
    email: SITE.email,
    taxID: SITE.rnc,
    foundingDate: String(SITE.foundingYear),
    address: { '@type': 'PostalAddress', ...SITE.address },
    geo: { '@type': 'GeoCoordinates', ...SITE.geo },
    openingHours: SITE.openingHours,
    priceRange: 'RD$',
    areaServed: SITE.areaServed.map((name) => ({ '@type': 'Place', name })),
    knowsAbout: [
        'Desarrollo de software a la medida',
        'Desarrollo web',
        'Drupal',
        'WordPress',
        'React',
        'React Native',
        'Node.js',
        'Facturación electrónica DGII',
        'Comprobantes fiscales electrónicos (e-CF)',
        'CRM',
        'Punto de venta (POS)',
        'eCommerce',
        'SEO',
        'Google Ads',
        'Automatización con inteligencia artificial',
    ],
    contactPoint: [
        {
            '@type': 'ContactPoint',
            telephone: SITE.phone,
            contactType: 'sales',
            email: SITE.email,
            availableLanguage: ['Spanish', 'English'],
            areaServed: 'DO',
        },
        {
            '@type': 'ContactPoint',
            telephone: SITE.phone,
            contactType: 'customer support',
            email: SITE.email,
            availableLanguage: ['Spanish'],
            areaServed: 'DO',
        },
    ],
    ...(SITE.sameAs.length ? { sameAs: SITE.sameAs } : {}),
});

export const websiteSchema = () => ({
    '@context': CONTEXT,
    '@type': 'WebSite',
    '@id': `${SITE.url}/#website`,
    url: `${SITE.url}/`,
    name: SITE.name,
    description: SITE.defaultDescription,
    inLanguage: SITE.language,
    publisher: { '@id': `${SITE.url}/#organization` },
});

export const webPageSchema = ({ path, title, description, type = 'WebPage' }) => ({
    '@context': CONTEXT,
    '@type': type,
    '@id': `${absoluteUrl(path)}#webpage`,
    url: absoluteUrl(path),
    name: title,
    description,
    inLanguage: SITE.language,
    isPartOf: { '@id': `${SITE.url}/#website` },
    about: { '@id': `${SITE.url}/#organization` },
});

export const breadcrumbSchema = (items) => ({
    '@context': CONTEXT,
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: absoluteUrl(item.path),
    })),
});

export const faqSchema = (faqs) => ({
    '@context': CONTEXT,
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
});

export const serviceSchema = ({ path, name, description, serviceType, offers }) => ({
    '@context': CONTEXT,
    '@type': 'Service',
    '@id': `${absoluteUrl(path)}#service`,
    name,
    description,
    serviceType,
    url: absoluteUrl(path),
    provider: { '@id': `${SITE.url}/#organization` },
    areaServed: SITE.areaServed.map((n) => ({ '@type': 'Place', name: n })),
    availableChannel: {
        '@type': 'ServiceChannel',
        serviceUrl: `${SITE.url}/#contactenos`,
        servicePhone: SITE.phone,
        availableLanguage: ['Spanish', 'English'],
    },
    ...(offers ? { offers } : {}),
});

export const softwareApplicationSchema = ({ path, name, description, plans }) => ({
    '@context': CONTEXT,
    '@type': 'SoftwareApplication',
    '@id': `${absoluteUrl(path)}#software`,
    name,
    description,
    url: absoluteUrl(path),
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'Facturación electrónica y contabilidad',
    operatingSystem: 'Web',
    inLanguage: SITE.language,
    provider: { '@id': `${SITE.url}/#organization` },
    offers: plans.map((plan) => ({
        '@type': 'Offer',
        name: `Plan ${plan.name}`,
        price: String(plan.price).replace(/,/g, ''),
        priceCurrency: 'DOP',
        priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: String(plan.price).replace(/,/g, ''),
            priceCurrency: 'DOP',
            unitText: 'mes',
        },
        description: plan.features.join(', '),
        availability: 'https://schema.org/InStock',
        url: absoluteUrl(path),
    })),
});
